import React, { useEffect, useRef } from 'react';

const RippleBg = ({ accentColor = '#c2ffe0', isDark = true, pageBg }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let raf;

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    window.addEventListener('resize', resize);
    resize();

    const hex2rgb = (hex) => {
      const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return r ? [parseInt(r[1], 16), parseInt(r[2], 16), parseInt(r[3], 16)] : [194, 255, 224];
    };
    const [ar, ag, ab] = hex2rgb(accentColor);

    const rings = Array.from({ length: 6 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 80,
      maxR: 220 + Math.random() * 180,
      speed: 0.5 + Math.random() * 0.8,
      alpha: 0,
    }));

    let t = 0;
    const draw = () => {
      t += 0.012;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // ✅ Use pageBg as the base, then overlay the ripple gradient on top
      const userBg = pageBg || (isDark ? '#060a12' : '#c8e0ff');
      ctx.fillStyle = userBg;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Subtle radial tint on top of the user's chosen bg
      const tint = ctx.createRadialGradient(
        canvas.width * 0.5, canvas.height * 0.5, 0,
        canvas.width * 0.5, canvas.height * 0.5, canvas.width * 0.8
      );
      tint.addColorStop(0, `rgba(${ar},${ag},${ab},${isDark ? 0.04 : 0.08})`);
      tint.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = tint;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      rings.forEach((ring) => {
        ring.r += ring.speed;
        ring.alpha = isDark
          ? (1 - ring.r / ring.maxR) * 0.35
          : (1 - ring.r / ring.maxR) * 0.25;

        if (ring.r >= ring.maxR) {
          ring.r = 0;
          ring.x = Math.random() * canvas.width;
          ring.y = Math.random() * canvas.height;
          ring.maxR = 220 + Math.random() * 180;
          ring.speed = 0.5 + Math.random() * 0.8;
        }

        ctx.beginPath();
        ctx.arc(ring.x, ring.y, ring.r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${ar},${ag},${ab},${ring.alpha})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();

        if (ring.r < 40) {
          const g = ctx.createRadialGradient(ring.x, ring.y, 0, ring.x, ring.y, 40);
          g.addColorStop(0, `rgba(${ar},${ag},${ab},${isDark ? 0.15 : 0.1})`);
          g.addColorStop(1, `rgba(${ar},${ag},${ab},0)`);
          ctx.beginPath();
          ctx.arc(ring.x, ring.y, 40, 0, Math.PI * 2);
          ctx.fillStyle = g;
          ctx.fill();
        }
      });

      ctx.strokeStyle = `rgba(${ar},${ag},${ab},${isDark ? 0.03 : 0.06})`;
      ctx.lineWidth = 1;
      const step = 60;
      for (let x = 0; x < canvas.width; x += step) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke();
      }
      for (let y = 0; y < canvas.height; y += step) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvas.width, y); ctx.stroke();
      }

      raf = requestAnimationFrame(draw);
    };

    draw();
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(raf); };
  }, [accentColor, isDark, pageBg]);

  return <canvas ref={canvasRef} className="fixed inset-0 z-0 pointer-events-none" />;
};

export default RippleBg;